import {Injectable} from '@angular/core';
import {Observable, pipe, Subject} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {
    convertDexOrderTxFromApi,
    DexOrderTx,
    DexOrderTxFromApi
} from '../../../../widgets/dex-tx-history-widget/tx-storage/dex-order-tx';
import type {WebSocketMessage} from '../../../streaming/streaming-interfaces';
import {SubscriptionSubjectType} from '../../../streaming/subscription-subject-type';
import {DexStreamingSubjectService} from './dex-subjects-injection';

enum DexStreamingOrderTxMsgType {
    ORDER_TRANSACTION_SNAPSHOT = 'ORDER_TRANSACTION_SNAPSHOT',
    ORDER_TRANSACTION_UPDATE   = 'ORDER_TRANSACTION_UPDATE'
}

export interface DexStreamingOrderTxSnapshotMsgData {
    requestId: number | null;
    data: DexOrderTxFromApi[];
}

interface DexStreamingOrderTxSnapshotMsg {
    messageType: DexStreamingOrderTxMsgType.ORDER_TRANSACTION_SNAPSHOT;
    data: DexStreamingOrderTxSnapshotMsgData;
}

interface DexStreamingOrderTxUpdateMsg {
    messageType: DexStreamingOrderTxMsgType.ORDER_TRANSACTION_UPDATE;
    data: DexOrderTxFromApi[];
}

type SubjectMsg = DexStreamingOrderTxSnapshotMsg | DexStreamingOrderTxUpdateMsg;

@Injectable()
export class DexOrderTxSubjectService implements DexStreamingSubjectService<DexOrderTx[]> {

    public readonly type: SubscriptionSubjectType<DexOrderTx[]>;

    public readonly orderTxUpdated$: Observable<DexOrderTx>;
    public readonly orderTxUpdatesWithoutSnapshot$: Observable<DexOrderTx>;

    private subscriptionSentSubject = new Subject<any>();
    private disconnectedSubject = new Subject<any>();

    private readonly orderTxUpdatedSubject$ = new Subject<DexOrderTx>();
    private readonly orderTxUpdatesWithoutSnapshotSubject$ = new Subject<DexOrderTx>();

    constructor() {
        this.type = this.createType();
        this.orderTxUpdated$ = this.orderTxUpdatedSubject$.asObservable();
        this.orderTxUpdatesWithoutSnapshot$ = this.orderTxUpdatesWithoutSnapshotSubject$.asObservable();
    }

    private createType(): SubscriptionSubjectType<DexOrderTx[]> {
        return {
            name: 'DEX_ORDER_TX',
            isMultiSubject: true,
            operations: {
                SUBSCRIBE: 'SUBSCRIBE_ORDER_TRANSACTION',
                UNSUBSCRIBE: 'UNSUBSCRIBE_ORDER_TRANSACTION'
            },
            messagePipe: pipe(
                filter((message: WebSocketMessage) => message.messageType in DexStreamingOrderTxMsgType),
                map((message: SubjectMsg): DexOrderTx[] => this.readMessage(message))
            ),
            subscriptionSent$: this.subscriptionSentSubject,
            disconnected$: this.disconnectedSubject
        };
    }

    private readMessage(message: SubjectMsg): DexOrderTx[] {
        const result: DexOrderTx[] = [];
        let rawList: DexOrderTxFromApi[];
        let isSnapshot: boolean = false;

        try {
            switch (message.messageType) {
                case DexStreamingOrderTxMsgType.ORDER_TRANSACTION_SNAPSHOT: {
                    isSnapshot = true;
                    rawList = message.data.data;
                    break;
                }
                case DexStreamingOrderTxMsgType.ORDER_TRANSACTION_UPDATE: {
                    rawList = message.data;
                    break;
                }
            }

            if (rawList) {
                for (const oRaw of rawList) {
                    try {
                        const o = convertDexOrderTxFromApi(oRaw);

                        this.orderTxUpdatedSubject$.next(o);

                        if (!isSnapshot) {
                            this.orderTxUpdatesWithoutSnapshotSubject$.next(o);
                        }

                        result.push(o);
                    } catch (e) {
                        console.error(`Error converting item from ${message.messageType}`, e, {message, item: oRaw});
                    }
                }
            }
        } catch (e) {
            console.error(`Error while reading ${message.messageType}`, e, message);
        }

        return result;
    }

}
